import type { Lot } from '@gozoki/api-types'
import { Chip, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import z from 'zod/v3'
import SimpleDataGrid from '~/components/containers/SimpleDataGrid'
import { QualityPageLayout } from '~/components/routes/quality/quality-page-layout'
import {
  buildQualitySharedQuery,
  type QualitySharedSearch,
  qualitySharedSearchSchema,
  qualityStatuses,
  resolveQualitySharedFilters,
} from '~/components/routes/quality/quality-search'
import { QUALITY_QUERY_CACHE } from '~/components/routes/quality/qualityUtils'
import { QUALITY_TAB_PERMISSIONS } from '~/components/routes/quality/tabs'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { formatDateOnly } from '~/utils/types/formatDates'

export const Route = createFileRoute('/_authenticated/quality/lots')({
  staticData: {
    permission: QUALITY_TAB_PERMISSIONS.lots,
  },
  component: QualityLotsPage,
  validateSearch: qualitySharedSearchSchema.extend({
    lotId: z.string().optional(),
    status: z.enum(qualityStatuses).optional(),
  }),
})
withRoutePermissions(Route)

const LotStatus = ({ lot }: { lot: Lot }) => {
  let title = 'Non bloqué'
  if (lot.blocked) {
    title = 'Bloqué'
  }
  if (!lot.blocked && lot.blockedAt) {
    title = 'Débloqué'
  }
  return <Chip style={{ height: 35 }} label={title} color={lot.blocked ? 'error' : 'success'} />
}

const qualityColumnDef = (): GridColDef[] => [
  {
    field: 'lot',
    headerName: 'N° de lot',
    width: 200,
  },
  {
    field: 'ean',
    headerName: 'EAN 13',
    width: 180,
  },
  { field: 'nb', headerName: "Nb d'articles", width: 100 },
  {
    field: 'label',
    headerName: 'Libellé',
    width: 350,
  },
  {
    field: 'dlc',
    headerName: 'DLC',
    valueFormatter: (params) => formatDateOnly(params.value as string),
    width: 180,
  },
  {
    field: 'tracking',
    headerName: 'Infos de traçabilité',
    width: 200,
  },
  {
    field: 'lo',
    headerName: 'Statut',
    renderCell: (params) => <LotStatus lot={params.row.lotDetail as Lot} />,
    width: 150,
  },
]

function QualityLotsPage() {
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { lotId = null, status = 'all' } = search

  const filters = resolveQualitySharedFilters(search)
  const { page, pageSize } = filters

  const handleSearchChange = (patch: Partial<QualitySharedSearch>) => {
    navigate({ search: (prev) => ({ ...prev, ...patch }) })
  }

  const {
    data: lots,
    isLoading,
    isFetching,
  } = useQuery(
    api.stores.getLotsData.queryOptions(
      {
        query: {
          ...buildQualitySharedQuery(filters),
          lot: lotId ?? undefined,
          specials: false,
          status,
        },
      },
      { placeholderData: keepPreviousData, ...QUALITY_QUERY_CACHE },
    ),
  )

  const safeLots = lots?.data ?? []

  const handleLotClick = (lot: Lot) => {
    navigate({ params: { lot: serializeLot(lot) }, to: '/quality/$lot' })
  }

  return (
    <QualityPageLayout
      activeTab="lots"
      filters={filters}
      onSearchChange={handleSearchChange}
      searchField={
        <TextField
          variant="outlined"
          label="Recherche par n° de lot"
          value={lotId ?? ''}
          onChange={(e) => navigate({ search: (prev) => ({ ...prev, lotId: e.target.value }) })}
        />
      }
      statusField={
        <FormControl fullWidth>
          <InputLabel>Statut</InputLabel>
          <Select
            label="Statut"
            value={status}
            onChange={(e) =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  status: e.target.value as (typeof qualityStatuses)[number],
                }),
              })
            }
          >
            <MenuItem value={qualityStatuses[0]}>Tous</MenuItem>
            <MenuItem value={qualityStatuses[1]}>Bloqué</MenuItem>
            <MenuItem value={qualityStatuses[2]}>Non bloqué</MenuItem>
          </Select>
        </FormControl>
      }
    >
      <SimpleDataGrid
        columns={qualityColumnDef()}
        rows={safeLots.map((l) => ({
          id: `${page}-${l.lot}-${l.ean}-${l.dlc}-${l.comment}-${l.tracking}-${l.blocked}`,
          lot: l.lot,
          ean: l.ean,
          nb: l.nb,
          label: l.label,
          dlc: l.dlc,
          tracking: l.tracking,
          lotDetail: l,
        }))}
        loading={isLoading || isFetching}
        rowCount={lots?.meta.total ?? 0}
        pageSizeOptions={[20, 50]}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(params) => {
          handleSearchChange({ page: params.page, pageSize: params.pageSize })
        }}
        disableRowSelectionOnClick
        checkboxSelection={false}
        onRowClick={(row) => {
          if (row.row.lotDetail) {
            handleLotClick(row.row.lotDetail as Lot)
          }
        }}
      />
    </QualityPageLayout>
  )
}

function serializeLot(lot: Lot) {
  const lotData = JSON.stringify({
    dlc: lot.dlc,
    ean: lot.ean,
    lotNumber: lot.lot,
    tracking: lot.tracking,
  })

  const bytes = new TextEncoder().encode(lotData)
  const binaryString = String.fromCharCode(...bytes)

  // String -> base64 -> base64url
  return btoa(binaryString).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
